import { useState } from "react";
import axios from "axios";
import {
  Send,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Paperclip,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPANIES = [
  "Generali",
  "UnipolSai",
  "Allianz",
  "AXA",
  "Reale Mutua",
  "Italiana",
  "Zurich",
  "Cattolica",
  "Groupama",
  "HDI",
  "Vittoria",
  "ITAS",
  "Verti",
  "Prima",
  "Genial",
  "Altra",
];

const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export default function ReclamiForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    policy_number: "",
    company: COMPANIES[0],
    subject: "",
    description: "",
    accept_privacy: false,
  });
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onFilesChange = (e) => {
    const incoming = Array.from(e.target.files || []);
    const merged = [...files, ...incoming].slice(0, MAX_FILES);
    const filtered = merged.filter((f) => {
      if (f.size > MAX_FILE_SIZE) {
        setError(`Il file "${f.name}" supera i 5 MB.`);
        return false;
      }
      return true;
    });
    setFiles(filtered);
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.accept_privacy) {
      setStatus("error");
      setError("Devi spuntare l'accettazione del trattamento dei dati personali per inviare il reclamo.");
      return;
    }
    setStatus("loading");
    setError("");

    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("email", form.email.trim());
    fd.append("phone", form.phone.trim());
    fd.append("subject", form.subject.trim());
    fd.append("description", form.description.trim());
    fd.append("company", form.company);
    if (form.policy_number.trim()) fd.append("policy_number", form.policy_number.trim());
    fd.append("accept_privacy", "true");
    files.forEach((f) => fd.append("files", f));

    try {
      await axios.post(`${API}/reclami/submit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStatus("success");
      setForm({
        name: "",
        email: "",
        phone: "",
        policy_number: "",
        company: COMPANIES[0],
        subject: "",
        description: "",
        accept_privacy: false,
      });
      setFiles([]);
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Impossibile inviare il reclamo. Riprova più tardi.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="reclami-success" className="bg-white border border-slate-200 p-10 lg:p-14">
        <div className="w-14 h-14 bg-[color:var(--brand-blue)] flex items-center justify-center mb-6">
          <CheckCircle2 className="w-7 h-7 text-white" strokeWidth={2} />
        </div>
        <h3 className="font-heading text-2xl lg:text-3xl font-extrabold text-slate-900 mb-4 leading-tight">
          Reclamo inviato correttamente.
        </h3>
        <p className="text-slate-600 leading-relaxed mb-4">
          Abbiamo ricevuto la tua segnalazione e provvederemo a esaminarla con la massima
          attenzione, nel rispetto delle normative IVASS vigenti.
        </p>
        <p className="text-slate-600 leading-relaxed mb-8">
          Riceverai un riscontro all'indirizzo email indicato entro i tempi previsti dalla
          normativa.
        </p>
        <button onClick={() => setStatus("idle")} data-testid="reclami-new-btn" className="btn-outline">
          Invia un altro reclamo
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="reclami-form" className="bg-white border border-slate-200 p-8 lg:p-12 grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Nome e cognome" required>
        <input required minLength={2} maxLength={100} value={form.name} onChange={update("name")} data-testid="reclami-name-input" placeholder="Mario Rossi" className="input-90" />
      </Field>
      <Field label="Email" required>
        <input required type="email" value={form.email} onChange={update("email")} data-testid="reclami-email-input" placeholder="mario.rossi@email.it" className="input-90" />
      </Field>

      <Field label="Numero di telefono" required>
        <input required type="tel" minLength={5} maxLength={30} value={form.phone} onChange={update("phone")} data-testid="reclami-phone-input" placeholder="+39 333 12 34 567" className="input-90" />
      </Field>
      <Field label="Numero polizza (facoltativo)">
        <input maxLength={80} value={form.policy_number} onChange={update("policy_number")} data-testid="reclami-policy-input" placeholder="Es. 1234567890" className="input-90" />
      </Field>

      <Field label="Compagnia assicurativa" required>
        <select required value={form.company} onChange={update("company")} data-testid="reclami-company-select" className="input-90">
          {COMPANIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Oggetto del reclamo" required>
        <input required minLength={2} maxLength={200} value={form.subject} onChange={update("subject")} data-testid="reclami-subject-input" placeholder="Es. Mancata risposta su sinistro" className="input-90" />
      </Field>

      <div className="sm:col-span-2">
        <Field label="Descrizione dettagliata" required>
          <textarea required minLength={10} maxLength={8000} rows={7} value={form.description} onChange={update("description")} data-testid="reclami-description-textarea" placeholder="Descrivi la tua segnalazione fornendo tutti i dettagli utili: date, persone coinvolte, eventi, comunicazioni intercorse..." className="input-90 resize-y min-h-[180px]" />
        </Field>
      </div>

      {/* File upload */}
      <div className="sm:col-span-2">
        <span className="block text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
          Allegati <span className="text-slate-400 normal-case tracking-normal font-normal">(facoltativo · max 5 file · 5 MB ciascuno)</span>
        </span>
        <label
          data-testid="reclami-file-label"
          className="flex items-center gap-3 px-5 py-4 bg-slate-50 border border-dashed border-slate-300 hover:border-[color:var(--brand-blue)] hover:bg-white cursor-pointer transition-all"
        >
          <Paperclip className="w-5 h-5 text-[color:var(--brand-blue)]" />
          <span className="text-sm font-semibold text-slate-700">Aggiungi file</span>
          <span className="text-xs text-slate-500 ml-auto">PDF, JPG, PNG, DOC</span>
          <input
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,application/pdf,image/jpeg,image/png,image/webp,image/heic,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={onFilesChange}
            data-testid="reclami-files-input"
            className="hidden"
          />
        </label>
        {files.length > 0 && (
          <ul data-testid="reclami-files-list" className="mt-3 space-y-2">
            {files.map((f, idx) => (
              <li key={idx} className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200">
                <Paperclip className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <span className="text-sm text-slate-700 flex-1 truncate">{f.name}</span>
                <span className="text-xs text-slate-500">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                <button type="button" onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-600" aria-label="Rimuovi">
                  <X className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <label className="sm:col-span-2 flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 cursor-pointer hover:border-[color:var(--brand-blue)]">
        <input type="checkbox" required checked={form.accept_privacy} onChange={update("accept_privacy")} data-testid="reclami-privacy-checkbox" className="mt-1 w-4 h-4 accent-[color:var(--brand-blue)]" />
        <span className="text-sm text-slate-700 leading-relaxed">
          Ho letto e accetto l'<a href="#documenti" className="text-[color:var(--brand-blue)] font-semibold underline-offset-2 hover:underline">Informativa Privacy</a> ai sensi del Regolamento UE 2016/679 (GDPR) e autorizzo 90 Brokers Srls al trattamento dei miei dati personali per la gestione del reclamo. <span className="text-[color:var(--brand-blue)] font-bold">*</span>
        </span>
      </label>

      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
        <p className="text-xs text-slate-500 leading-relaxed max-w-md">
          I reclami saranno gestiti nel rispetto delle normative IVASS vigenti.
        </p>
        <button type="submit" disabled={status === "loading"} data-testid="reclami-submit-btn" className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed shrink-0">
          {status === "loading" ? (<><Loader2 className="w-4 h-4 animate-spin" /> Invio in corso…</>) : (<><Send className="w-4 h-4" /> Invia Reclamo</>)}
        </button>
      </div>

      {status === "error" && error && (
        <div data-testid="reclami-error" className="sm:col-span-2 flex items-start gap-3 p-4 bg-red-50 border border-red-200 text-red-800 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
        {label} {required && <span className="text-[color:var(--brand-blue)]">*</span>}
      </span>
      {children}
    </label>
  );
}
